import {
  DEFAULT_LIST_INITIAL_STATE,
  DEFAULT_LIST_STORAGE,
  LIST_TABLE_APPEARANCE,
} from '@app/common/listTableAppearance';
import { decideDoctorInstruction, listDoctorInstructionsForOffice } from '@app/features/doctorPortal/api';
import { INSTRUCTION_PRIORITY_COLOR, INSTRUCTION_STATUS_COLOR } from '@app/features/doctorPortal/instructionTagColors';
import type { DoctorInstruction, DoctorInstructionStatus } from '@app/features/doctorPortal/types';
import { APP_PREFIX, VISITS_PAGE } from '@app/routes/urls';
import { RowAction, TableColumn } from '@konnecta/generic-table';
import { AppDataTable } from '@app/components/common/AppDataTable';
import { useLastListParams } from '@app/features/exports/useLastListParams';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { Alert, App as AntdApp, Button, Input, Modal, Segmented, Tag, Tooltip, Typography } from 'antd';
import dayjs from 'dayjs';
import type { TFunction } from 'i18next';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';

/**
 * The administrator's queue for the instructions doctors propose on a visit.
 *
 * An instruction reaches nobody until it is decided here: the caregiver's RLS policy
 * shows HANDLED instructions only, so an undecided one is invisible to the person meant
 * to carry it out. This screen is the only place that decision is made.
 *
 * Deciding is once and for all — the backend refuses a second decision with a 404
 * — so the actions are behind a confirmation that says what handling does, and a
 * decided row never offers them again. A failure here usually means a colleague
 * decided the row first, which is why the error path refetches instead of leaving
 * a stale queue on screen.
 */

// Renamed with everything else. It keys the saved column preferences, so any
// that exist are reset once - acceptable on a page that has never had a row.
const TABLE_ID = 'doctor-instruction-queue';
const UNSET = '—';

/** The status filter lives in the URL, so the queue is shareable and survives a reload. */
const STATUS_PARAM = 'status';

const STATUSES: DoctorInstructionStatus[] = ['PROCESSING', 'HANDLED', 'NOT_HANDLED'];
const DEFAULT_STATUS: DoctorInstructionStatus = 'PROCESSING';

/**
 * One instruction, named by the notification that led here.
 *
 * A notification about an instruction used to land on the bare queue, which
 * defaults to PROCESSING - so by the time anyone read it, the instruction it was
 * about had usually been decided and the reader got an empty list saying nothing
 * was waiting. Both params together narrow the list to THAT VISIT with no status
 * filter at all, so the instruction is on screen whatever was decided, and the
 * instruction id picks it out among the visit's others.
 */
const FOCUS_INSTRUCTION_PARAM = 'instruction';
const FOCUS_VISIT_PARAM = 'visit';

/** A positive integer id from the URL, or nothing - `Number('')` is 0, and 0 is no id. */
const parseId = (raw: string | null): number | null => {
  const value = Number(raw);
  return Number.isInteger(value) && value > 0 ? value : null;
};

const QUEUE_URL_PARAM_CONFIG = {
  // No `searchTerm`: GET /doctor-instructions takes no query, and the table renders the
  // search box only for a list that can act on it.
  external: { status: STATUS_PARAM },
} as const;

type PendingDecision = { instruction: DoctorInstruction; status: 'HANDLED' | 'NOT_HANDLED' };

/** Read the filter off a URL param / table param, falling back to the undecided queue. */
const parseQueueStatus = (raw: unknown): DoctorInstructionStatus =>
  typeof raw === 'string' && (STATUSES as string[]).includes(raw) ? (raw as DoctorInstructionStatus) : DEFAULT_STATUS;

const formatDate = (value?: string | null) => (value ? dayjs(value).format('DD/MM/YYYY HH:mm') : UNSET);

/**
 * Proposed / approved / rejected, held in the URL like the other lists' filters.
 *
 * The default (the undecided queue) is left OUT of the URL rather than written
 * into it, so a bare link means the same thing as the page's own default.
 */
const DoctorInstructionStatusFilter: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const handleChange = React.useCallback(
    (next: DoctorInstructionStatus) => {
      const nextParams = new URLSearchParams(searchParams);

      if (next === DEFAULT_STATUS) {
        nextParams.delete(STATUS_PARAM);
      } else {
        nextParams.set(STATUS_PARAM, next);
      }

      // Row counts change with the filter, so an offset page would land out of range.
      nextParams.set('page', '1');
      setSearchParams(nextParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  return (
    <div className="flex items-center gap-2">
      <span className="text-[12px] text-slate-500">{t('doctor-instruction-queue-filter-status')}</span>
      <Segmented<DoctorInstructionStatus>
        value={parseQueueStatus(searchParams.get(STATUS_PARAM))}
        onChange={handleChange}
        options={STATUSES.map((option) => ({
          label: t(`doctor-instruction-status-${option.toLowerCase()}`),
          value: option,
        }))}
      />
    </div>
  );
};

/** What the reader is looking at, and the way back to the whole queue. */
const FocusNotice: React.FC<{ onClear: () => void }> = ({ onClear }) => {
  const { t } = useTranslation();

  return (
    <div className="flex items-center gap-2">
      <span className="text-[12px] text-slate-500">{t('doctor-instruction-queue-focus-label')}</span>
      <Button size="small" onClick={onClear}>
        {t('doctor-instruction-queue-focus-clear')}
      </Button>
    </div>
  );
};

export const NOT_EXPORTED_DOCTOR_INSTRUCTIONS = ['actions'] as const;

export const getDoctorInstructionQueueColumns = (t: TFunction): TableColumn<DoctorInstruction>[] => [
  {
    id: 'title',
    header: t('doctor-instruction-queue-col-title'),
    accessor: 'title',
    minWidth: 260,
    cellRenderer: (_value, instruction) => (
      <div className="min-w-0">
        <Typography.Text strong>{instruction.title}</Typography.Text>
        {instruction.details && (
          <div className="text-[12px] text-slate-500 whitespace-pre-wrap">{instruction.details}</div>
        )}
      </div>
    ),
  },
  {
    // Approving a clinical instruction without seeing whose it is was the gap here:
    // the row used to carry a visit id and nothing else. The name is joined
    // through the visit, so it is absent rather than wrong when the caller
    // cannot read the client — say "unknown", never show a blank.
    id: 'patient',
    header: t('doctor-instruction-queue-col-patient'),
    accessor: 'clientLastName',
    width: 180,
    align: 'left',
    cellRenderer: (_value, instruction) => {
      const name = `${instruction.clientLastName ?? ''} ${instruction.clientFirstName ?? ''}`.trim();
      return name ? (
        <Typography.Text strong>{name}</Typography.Text>
      ) : (
        <Typography.Text type="secondary">{t('doctor-instruction-queue-patient-unknown')}</Typography.Text>
      );
    },
  },
  {
    id: 'visitId',
    header: t('doctor-instruction-queue-col-visit'),
    accessor: 'visitId',
    width: 110,
    cellRenderer: (_value, instruction) => (
      <Link to={`/${APP_PREFIX}/${VISITS_PAGE}/${instruction.visitId}/edit`}>#{instruction.visitId}</Link>
    ),
  },
  {
    id: 'priority',
    header: t('doctor-instruction-queue-col-priority'),
    accessor: 'priority',
    width: 130,
    cellRenderer: (_value, instruction) => (
      <Tag color={INSTRUCTION_PRIORITY_COLOR[instruction.priority]} className="!m-0">
        {t(`doctor-instruction-priority-${instruction.priority.toLowerCase()}`)}
      </Tag>
    ),
  },
  {
    id: 'status',
    header: t('doctor-instruction-queue-col-status'),
    accessor: 'status',
    width: 220,
    align: 'left',
    cellRenderer: (_value, instruction) => (
      <div className="flex flex-col gap-1 items-start">
        <Tag color={INSTRUCTION_STATUS_COLOR[instruction.status]} className="!m-0">
          {t(`doctor-instruction-status-${instruction.status.toLowerCase()}`)}
        </Tag>
        {instruction.decidedAt && (
          <span className="text-[11px] text-slate-400">
            {t('doctor-instruction-queue-decided-by', { date: formatDate(instruction.decidedAt) })}
          </span>
        )}
        {instruction.decisionNote && <span className="text-[11px] text-slate-500">{instruction.decisionNote}</span>}
      </div>
    ),
  },
  {
    id: 'createdAt',
    header: t('doctor-instruction-queue-col-created'),
    accessor: 'createdAt',
    width: 150,
    cellRenderer: (_value, instruction) => formatDate(instruction.createdAt),
  },
];

const DoctorInstructionQueuePage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { message } = AntdApp.useApp();
  const [searchParams, setSearchParams] = useSearchParams();

  const focusInstructionId = parseId(searchParams.get(FOCUS_INSTRUCTION_PARAM));
  const focusVisitId = parseId(searchParams.get(FOCUS_VISIT_PARAM));

  const clearFocus = React.useCallback(() => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete(FOCUS_INSTRUCTION_PARAM);
    nextParams.delete(FOCUS_VISIT_PARAM);
    // The queue is a different, longer list than one visit's instructions.
    nextParams.set('page', '1');
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const [pending, setPending] = React.useState<PendingDecision | null>(null);
  const [note, setNote] = React.useState('');
  const [deciding, setDeciding] = React.useState(false);
  const [loadFailed, setLoadFailed] = React.useState(false);
  const [refreshToken, setRefreshToken] = React.useState(0);
  const { remember, getParams } = useLastListParams();

  const closeDialog = () => {
    setPending(null);
    setNote('');
  };

  const fetchInstructions = React.useCallback(
    async (params: { limit: number; offset: number; status?: unknown; [key: string]: unknown }) => {
      try {
        // Focused: this visit's instructions, ALL statuses. A status filter here
        // is what made the notification's link land on an empty list.
        const response = await listDoctorInstructionsForOffice(
          focusVisitId
            ? { visitId: focusVisitId, offset: params.offset, limit: params.limit }
            : { status: parseQueueStatus(params.status), offset: params.offset, limit: params.limit },
          remember
        );
        setLoadFailed(false);
        return { data: response.data, total: response.total ?? response.data.length };
      } catch (error) {
        // An empty table reads as "nothing to decide", which is the one thing a
        // failed load must not be mistaken for — say so above the list.
        setLoadFailed(true);
        throw error;
      }
    },
    [focusVisitId, remember]
  );

  const decide = React.useCallback(
    async (decision: PendingDecision, decisionNote?: string) => {
      setDeciding(true);
      try {
        await decideDoctorInstruction(decision.instruction.instructionId, { status: decision.status, decisionNote });
        message.success(
          t(decision.status === 'HANDLED' ? 'doctor-instruction-queue-handled' : 'doctor-instruction-queue-not-handled')
        );
        closeDialog();
      } catch {
        // The row may have been decided by someone else since this page loaded.
        message.error(t('doctor-instruction-queue-decide-failed'));
        closeDialog();
      } finally {
        setDeciding(false);
        // Either way the queue on screen may be out of date, so it is read again
        // rather than left stale.
        setRefreshToken((token) => token + 1);
      }
    },
    [message, t]
  );

  const columns = React.useMemo(
    () => getDoctorInstructionQueueColumns(t),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, i18n.language]
  );

  const tableConfig = React.useMemo(
    () => ({
      tableId: TABLE_ID,
      initialState: DEFAULT_LIST_INITIAL_STATE,
      storage: DEFAULT_LIST_STORAGE,
      urlParamConfig: QUEUE_URL_PARAM_CONFIG,
      fetchData: fetchInstructions,
      rowKey: 'instructionId' as const,
      columns,
      // "Nothing is waiting to be handled" is the wrong sentence for a list
      // narrowed to one instruction that is not there any more.
      emptyState: {
        title: t(focusVisitId ? 'doctor-instruction-queue-focus-empty' : 'doctor-instruction-queue-empty'),
      },
      // The visit may carry several instructions; the one the notification was
      // about is the one that gets picked out. An inline style rather than a
      // class: the row's own background is set the same way, and a Tailwind
      // class loses to it.
      styleConfig: focusInstructionId
        ? {
            rowStyle: (row: DoctorInstruction) =>
              row.instructionId === focusInstructionId ? { backgroundColor: '#fef9c3' } : {},
          }
        : undefined,
      actions: {
        // Only a still-undecided instruction can be decided; the backend answers a
        // second decision with a 404, so offering the buttons would be a lie.
        getActions: (instruction: DoctorInstruction): RowAction<DoctorInstruction>[] =>
          instruction.status === 'PROCESSING'
            ? [
                {
                  key: 'approve',
                  label: t('doctor-instruction-queue-handle'),
                  onClick: () => setPending({ instruction, status: 'HANDLED' }),
                },
                {
                  key: 'reject',
                  label: t('doctor-instruction-queue-not-handle'),
                  danger: true,
                  onClick: () => setPending({ instruction, status: 'NOT_HANDLED' }),
                },
              ]
            : [],
        // Deciding is what this list is for, so both actions stay on the row
        // rather than behind a menu, with approve carrying the primary emphasis.
        render: ({ row, actions }: { row: DoctorInstruction; actions: RowAction<DoctorInstruction>[] }) => (
          <div className="flex gap-2 justify-center">
            {actions.map((action) => (
              <Tooltip key={action.key} title={action.label}>
                <Button
                  size="small"
                  shape="circle"
                  aria-label={typeof action.label === 'string' ? action.label : undefined}
                  color={action.danger ? 'danger' : 'green'}
                  variant="solid"
                  icon={action.danger ? <CloseOutlined /> : <CheckOutlined />}
                  onClick={(event) => {
                    event.stopPropagation();
                    action.onClick?.(row);
                  }}
                />
              </Tooltip>
            ))}
          </div>
        ),
        column: { header: t('actions-capital'), sticky: 'right' as const, align: 'center' as const, width: 96 },
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columns, fetchInstructions, focusInstructionId, focusVisitId, t, i18n.language]
  );

  const handling = pending?.status === 'HANDLED';

  return (
    // The layout's content area is `overflow-hidden` and every page owns its own
    // scrolling; `min-h-0` is what lets the table scroll inside this column
    // instead of pushing the page past the fold.
    <div className="flex-1 min-h-0 flex flex-col gap-2">
      {loadFailed && <Alert type="error" showIcon message={t('doctor-instruction-queue-load-failed')} />}

      <div className="flex-1 min-h-0">
        <AppDataTable
          appearance={LIST_TABLE_APPEARANCE}
          config={tableConfig}
          // A refresh token, not a `key`: remounting would throw away the filter
          // and the page the coordinator is on. It names the snapshot the rows
          // came from, which is what the table diffs to decide it must fetch again
          // - so the focused visit belongs in it, or clearing the focus would
          // leave one visit's rows on screen under the whole queue's filter.
          refreshToken={`${refreshToken}:${focusVisitId ?? ''}`}
          export={{ list: 'doctor_instructions', getParams, notExported: [...NOT_EXPORTED_DOCTOR_INSTRUCTIONS] }}
          // The status filter does not apply to a list narrowed to one
          // instruction - it is the filter that hid it in the first place.
          leftContent={focusVisitId ? <FocusNotice onClear={clearFocus} /> : <DoctorInstructionStatusFilter />}
        />
      </div>

      <Modal
        open={pending !== null}
        title={t(handling ? 'doctor-instruction-queue-handle-title' : 'doctor-instruction-queue-not-handle-title')}
        okText={t(handling ? 'doctor-instruction-queue-handle' : 'doctor-instruction-queue-not-handle')}
        cancelText={t('common-cancel')}
        okButtonProps={{ danger: !handling, loading: deciding }}
        onCancel={closeDialog}
        onOk={() => pending && decide(pending, note.trim() || undefined)}
        destroyOnHidden
      >
        <div className="flex flex-col gap-3">
          <Typography.Text>{pending?.instruction.title}</Typography.Text>
          <Typography.Text type="secondary">
            {t(handling ? 'doctor-instruction-queue-handle-body' : 'doctor-instruction-queue-not-handle-body')}
          </Typography.Text>
          <div>
            <div className="text-[12px] text-slate-500 mb-1">{t('doctor-instruction-queue-note-label')}</div>
            <Input.TextArea
              rows={3}
              value={note}
              onChange={(event) => setNote(event.target.value)}
              aria-label={t('doctor-instruction-queue-note-label')}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default DoctorInstructionQueuePage;
